import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Activity, RefreshCw, Loader2, Factory, Wrench, Gauge,
    WifiOff, AlertOctagon, Siren, ChevronRight, AlertTriangle,
    CalendarClock, Zap, TrendingUp, TrendingDown, MapPin,
    ExternalLink, Inbox, Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const DISPLAY_FONT = { fontFamily: "'Chakra Petch', 'Segoe UI', system-ui, sans-serif" };
const nf = new Intl.NumberFormat('ru-RU');

const plural = (n, one, few, many) => {
    const abs = Math.abs(n) % 100;
    const d = abs % 10;
    if (abs > 10 && abs < 20) return many;
    if (d === 1) return one;
    if (d >= 2 && d <= 4) return few;
    return many;
};

const useCountUp = (target, duration = 900) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (target == null || Number.isNaN(target)) return;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return target == null ? null : value;
};

const AnimatedNumber = ({ value, className = '' }) => {
    const v = useCountUp(value);
    return (
        <span className={`tabular-nums ${className}`} style={DISPLAY_FONT}>
            {v == null ? '—' : nf.format(v)}
        </span>
    );
};

const formatAddress = (d) => {
    const parts = [];
    if (d.city) parts.push(d.city);
    if (d.street) parts.push(d.street);
    if (d.houseNumber) parts.push(`д. ${d.houseNumber}`);
    return parts.length ? parts.join(', ') : 'Адрес не указан';
};

const StatisticsPage = () => {
    const navigate = useNavigate();

    // ===== Основная сводка =====
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // ===== Аномалии =====
    const [anomalies, setAnomalies] = useState(null);
    const [anomaliesLoading, setAnomaliesLoading] = useState(true);
    const [anomaliesError, setAnomaliesError] = useState(null);
    const [anomaliesFromCache, setAnomaliesFromCache] = useState(false);

    // ===== Поверка узлов учета =====
    const [verificationData, setVerificationData] = useState(null);
    const [verificationLoading, setVerificationLoading] = useState(true);
    const [verificationError, setVerificationError] = useState(null);

    const loadStats = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        try {
            const { data } = await axios.get(`${API_URL}/api/statistics/summary`, { headers });
            setStats({
                totalDevices: data.totalDevices ?? null,
                servicedDevices: data.servicedDevices ?? null,
                lostCount: data.lostCount ?? null,
                criticalLostCount: data.criticalLostCount ?? null,
                devicesWithMultipleAlarms: data.devicesWithMultipleAlarms ?? null,
                alarmsScanDate: data.alarmsScanDate ?? null,
            });
            setLastUpdated(new Date());
        } catch (e) {
            try {
                const { data } = await axios.get(`${API_URL}/api/ns/lost-devices`, { headers });
                const list = data || [];
                setStats({
                    totalDevices: null, servicedDevices: null,
                    lostCount: list.length,
                    criticalLostCount: list.filter(d => (d.daysOffline ?? 0) >= 7).length,
                    devicesWithMultipleAlarms: null, alarmsScanDate: null,
                });
                setLastUpdated(new Date());
            } catch {
                setError('Не удалось загрузить сводные данные');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadAnomalies = useCallback(async () => {
        setAnomaliesLoading(true);
        setAnomaliesError(null);
        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const { data } = await axios.get(`${API_URL}/api/statistics/anomalies`, { headers });
            setAnomalies(data.devices || []);
            setAnomaliesFromCache(data.fromCache ?? false);
        } catch {
            setAnomaliesError('Не удалось загрузить данные об аномалиях');
        } finally {
            setAnomaliesLoading(false);
        }
    }, []);

    const loadVerification = useCallback(async () => {
        setVerificationLoading(true);
        setVerificationError(null);
        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
            const { data } = await axios.get(`${API_URL}/api/metering-units`, { headers });
            setVerificationData(data);
        } catch {
            setVerificationError('Не удалось загрузить данные о поверках');
        } finally {
            setVerificationLoading(false);
        }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadAnomalies(); }, [loadAnomalies]);
    useEffect(() => { loadVerification(); }, [loadVerification]);

    const goToNS = (tab) => {
        localStorage.setItem('nsActiveTab', tab);
        navigate('/ns');
    };

    const goToAnalysis = (device) => {
        localStorage.setItem('analysisSelectedDevice', JSON.stringify({
            name: device.deviceName, deviceType: device.deviceType,
            city: device.city, street: device.street,
            houseNumber: device.houseNumber, fullName: device.deviceName, status: 1,
        }));
        navigate('/analysis');
    };

    // ✅ Считаем количество узлов, не введенных в эксплуатацию (статус !== 1)
    const notCommissionedCount = useMemo(() => {
        if (!verificationData?.items) return 0;
        return verificationData.items.filter(node => node.status !== 1).length;
    }, [verificationData]);

    // ✅ Функция для перехода на вкладку поверок с применением фильтра по году
    const goToVerification = (year) => {
        if (year) {
            sessionStorage.setItem('meteringUnitsYearFilter', year.toString());
        }
        navigate('/verification-schedule?tab=metering');
    };

    const todayRaw = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'long' });
    const today = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);

    return (
        <div className="relative space-y-1">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@500;600;700&display=swap');
                @keyframes stat-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            {/* Фоновая подложка */}
            <div className="pointer-events-none absolute -inset-x-4 -top-4 h-72 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(55%_100%_at_50%_0%,rgba(59,130,246,0.10),transparent_70%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.04)_1px,transparent_1px)] bg-[size:34px_34px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
            </div>

            {/* ===== Шапка ===== */}
            <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
                style={{ animation: 'stat-fade-up .5s ease both' }}>
                <div>
                    <div className="flex items-center gap-2 text-blue-400/90 text-[11px] font-bold tracking-[0.22em] uppercase">
                        <Activity className="w-3.5 h-3.5" /> Оперативная сводка
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1.5" style={DISPLAY_FONT}>Статистика</h2>
                    <p className="text-slate-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
                        <CalendarClock className="w-4 h-4 text-slate-500" />
                        По состоянию на {today}
                        <span className="inline-flex items-center gap-1.5 text-emerald-400/90 text-xs font-medium">
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                            </span>
                            мониторинг активен
                        </span>
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {lastUpdated && (
                        <span className="text-slate-500 text-xs hidden sm:inline tabular-nums">
                            Обновлено: {lastUpdated.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                    )}
                    <button
                        onClick={() => { loadStats(); loadAnomalies(); loadVerification(); }}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 shadow-lg shadow-blue-600/20"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> Обновить
                    </button>
                </div>
            </div>

            {/* ===== Ошибка ===== */}
            {!isLoading && error && (
                <div className="relative bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}</span>
                    <button onClick={loadStats} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                </div>
            )}

            {/* ===== Загрузка ===== */}
            {isLoading && (
                <div className="relative space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl h-40 animate-pulse" />
                        <div className="lg:col-span-3 space-y-4">
                            <div className="bg-slate-800 border border-slate-700 rounded-xl h-28 animate-pulse" />
                            <div className="bg-slate-800 border border-slate-700 rounded-xl h-28 animate-pulse" />
                        </div>
                    </div>
                    <div className="text-slate-400 text-sm flex items-center gap-2 justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> Собираем сводку...
                    </div>
                </div>
            )}

            {/* ===== Контент ===== */}
            {!isLoading && !error && stats && (
                <>
                    <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-4">
                        {/* ---------- Парк устройств ---------- */}
                        <section className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition"
                            style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '60ms' }}>
                            <div className="h-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-transparent" />
                            <div className="p-4 sm:p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                                        <Factory className="w-4.5 h-4.5 text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>Парк устройств</h3>
                                        <p className="text-slate-500 text-[11px]">Приборы учёта в системе</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg px-3 py-3">
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <Gauge className="w-3 h-3 text-slate-500" /> Всего
                                        </div>
                                        <AnimatedNumber value={stats.totalDevices} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                    </div>
                                    <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg px-3 py-3">
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <Wrench className="w-3 h-3 text-emerald-500" /> Обслуживаемые
                                        </div>
                                        <AnimatedNumber value={stats.servicedDevices} className="text-2xl sm:text-3xl font-bold text-emerald-400 leading-none" />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* ---------- Нештатные ситуации ---------- */}
                        <div className="lg:col-span-3 flex flex-col gap-4">
                            <div className="flex items-center gap-2 text-slate-400 text-[11px] font-bold tracking-[0.22em] uppercase"
                                style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '120ms' }}>
                                <Siren className="w-3.5 h-3.5 text-red-400" /> Нештатные ситуации
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <section className="bg-slate-800 border border-slate-700 border-l-4 border-l-red-500 rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/25 transition-all"
                                    style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '160ms' }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                                            <WifiOff className="w-4.5 h-4.5 text-red-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <AnimatedNumber value={stats.lostCount} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                            <div className="text-slate-400 text-[11px] mt-0.5">без связи</div>
                                        </div>
                                    </div>
                                    {(stats.criticalLostCount ?? 0) > 0 && (
                                        <div className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 text-[11px] font-bold">
                                            <span className="relative flex h-1.5 w-1.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                                            </span>
                                            критично (7+ дн): {stats.criticalLostCount}
                                        </div>
                                    )}
                                    <button onClick={() => goToNS('lost')}
                                        className="mt-3 w-full px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 group/link">
                                        К вкладке «Устройства без связи»
                                        <ChevronRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                                    </button>
                                </section>

                                <section className="bg-slate-800 border border-slate-700 border-l-4 border-l-orange-500 rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/25 transition-all"
                                    style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '220ms' }}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                                            <AlertOctagon className="w-4.5 h-4.5 text-orange-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <AnimatedNumber value={stats.devicesWithMultipleAlarms} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                            <div className="text-slate-400 text-[11px] mt-0.5">с авариями (&gt;1)</div>
                                        </div>
                                    </div>
                                    <div className="text-slate-500 text-[11px] mt-2.5">
                                        {stats.devicesWithMultipleAlarms == null ? (<>откройте вкладку аварий для сканирования</>)
                                            : stats.alarmsScanDate ? (<>сканирование: {new Date(stats.alarmsScanDate).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</>)
                                                : (<>больше 1 активной аварии</>)}
                                    </div>
                                    <button onClick={() => goToNS('alarms')}
                                        className="mt-3 w-full px-3 py-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 hover:text-orange-200 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 group/link">
                                        К вкладке «Аварии и предупреждения»
                                        <ChevronRight className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                                    </button>
                                </section>
                            </div>
                        </div>
                    </div>

                    {/* ============================================================ */}
                    {/* ===== ПОВЕРКА УЗЛОВ УЧЕТА ================================== */}
                    {/* ============================================================ */}
                    <section className="relative bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                        style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '280ms' }}>
                        <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-transparent" />
                        <div className="p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                                        <Wrench className="w-4.5 h-4.5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>Поверка узлов учета</h3>
                                        <p className="text-slate-500 text-[11px]">контроль сроков поверок приборов</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => navigate('/verification-schedule?tab=metering')}
                                        className="px-3 py-1.5 bg-blue-600/15 hover:bg-blue-600/30 border border-blue-500/30 text-blue-300 hover:text-blue-200 rounded-lg text-xs font-semibold transition flex items-center gap-1.5">
                                        К вкладке «Поверка узлов учета» <ExternalLink className="w-3 h-3" />
                                    </button>
                                    <button onClick={loadVerification} disabled={verificationLoading}
                                        className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-50">
                                        <RefreshCw className={`w-3 h-3 ${verificationLoading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>

                            {verificationLoading && (
                                <div className="flex items-center justify-center py-10 gap-3">
                                    <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
                                    <span className="text-slate-400 text-xs">Загрузка данных...</span>
                                </div>
                            )}

                            {!verificationLoading && verificationError && (
                                <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {verificationError}</span>
                                    <button onClick={loadVerification} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                                </div>
                            )}

                            {!verificationLoading && !verificationError && verificationData && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg px-4 py-3">
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <Package className="w-3 h-3 text-slate-500" /> Всего узлов
                                        </div>
                                        <AnimatedNumber value={verificationData?.summary?.all ?? 0} className="text-2xl sm:text-3xl font-bold text-white leading-none" />
                                    </div>

                                    <div
                                        className="bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-red-500/10 transition"
                                        onClick={() => navigate('/verification-schedule?tab=metering')}
                                        title="Показать все узлы"
                                    >
                                        <div className="flex items-center gap-1.5 text-red-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <AlertTriangle className="w-3 h-3 text-red-500" /> Не введены
                                        </div>
                                        <AnimatedNumber value={notCommissionedCount} className="text-2xl sm:text-3xl font-bold text-red-400 leading-none" />
                                    </div>

                                    <div
                                        className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-amber-500/10 transition"
                                        onClick={() => goToVerification(verificationData?.currentYear || new Date().getFullYear())}
                                        title={`Открыть фильтр за ${verificationData?.currentYear || new Date().getFullYear()} год`}
                                    >
                                        <div className="flex items-center gap-1.5 text-amber-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <CalendarClock className="w-3 h-3 text-amber-500" /> Поверка в {verificationData?.currentYear || new Date().getFullYear()}
                                        </div>
                                        <AnimatedNumber value={verificationData?.summary?.currentYear ?? 0} className="text-2xl sm:text-3xl font-bold text-amber-400 leading-none" />
                                    </div>

                                    <div
                                        className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg px-4 py-3 cursor-pointer hover:bg-yellow-500/10 transition"
                                        onClick={() => goToVerification(verificationData?.nextYear || (new Date().getFullYear() + 1))}
                                        title={`Открыть фильтр за ${verificationData?.nextYear || (new Date().getFullYear() + 1)} год`}
                                    >
                                        <div className="flex items-center gap-1.5 text-yellow-400 text-[10px] font-medium uppercase tracking-wider mb-1">
                                            <CalendarClock className="w-3 h-3 text-yellow-500" /> Поверка в {verificationData?.nextYear || (new Date().getFullYear() + 1)}
                                        </div>
                                        <AnimatedNumber value={verificationData?.summary?.nextYear ?? 0} className="text-2xl sm:text-3xl font-bold text-yellow-400 leading-none" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ============================================================ */}
                    {/* ===== РЕЗКИЕ ИЗМЕНЕНИЯ ПОКАЗАНИЙ =========================== */}
                    {/* ============================================================ */}
                    <section className="relative bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                        style={{ animation: 'stat-fade-up .55s ease both', animationDelay: '340ms' }}>
                        <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-transparent" />
                        <div className="p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center">
                                        <Zap className="w-4.5 h-4.5 text-red-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-white font-semibold text-sm" style={DISPLAY_FONT}>Резкие изменения показаний</h3>
                                        <p className="text-slate-500 text-[11px]">порог ≥ 30% за 10 дней • сравнительный анализ</p>
                                    </div>
                                </div>
                                <button onClick={loadAnomalies} disabled={anomaliesLoading}
                                    className="self-start sm:self-auto px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 border border-slate-600 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-50">
                                    <RefreshCw className={`w-3 h-3 ${anomaliesLoading ? 'animate-spin' : ''}`} /> Обновить
                                </button>
                            </div>

                            {anomaliesLoading && (
                                <div className="flex flex-col items-center justify-center py-10 gap-3">
                                    <Loader2 className="w-6 h-6 animate-spin text-red-400" />
                                    <div className="text-slate-400 text-xs text-center">
                                        Сканирование суточных архивов всех устройств...<br />
                                        <span className="text-slate-500">может занять несколько минут при первом запуске</span>
                                    </div>
                                </div>
                            )}

                            {!anomaliesLoading && anomaliesError && (
                                <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> {anomaliesError}</span>
                                    <button onClick={loadAnomalies} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap">Повторить</button>
                                </div>
                            )}

                            {!anomaliesLoading && !anomaliesError && anomalies && anomalies.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-3">
                                        <Inbox className="w-6 h-6 text-green-400" />
                                    </div>
                                    <p className="text-slate-300 text-sm font-medium">Резких изменений не обнаружено</p>
                                    <p className="text-slate-500 text-xs mt-1">Все показания в пределах нормы (отклонения &lt; 10%)</p>
                                </div>
                            )}

                            {!anomaliesLoading && !anomaliesError && anomalies && anomalies.length > 0 && (
                                <>
                                    <div className="flex items-center gap-2 mb-3 text-xs text-slate-400">
                                        <span className="font-semibold text-white tabular-nums">{anomalies.length}</span>
                                        {plural(anomalies.length, 'устройство', 'устройства', 'устройств')} с резкими изменениями
                                        {anomaliesFromCache && <span className="ml-auto text-slate-600 text-[10px] uppercase tracking-wider">кэш 30 мин</span>}
                                    </div>
                                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar">
                                        {anomalies.map((device, idx) => (
                                            <div key={`${device.deviceName}-${idx}`}
                                                className="bg-slate-900/50 border border-slate-700/60 border-l-4 border-l-red-500/70 rounded-lg px-4 py-3 hover:border-slate-600 hover:bg-slate-900/80 transition group">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        {device.maxChangePercent > 0
                                                            ? <TrendingUp className="w-4 h-4 text-red-400" />
                                                            : <TrendingDown className="w-4 h-4 text-blue-400" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-white text-sm font-semibold truncate">{device.deviceName}</span>
                                                            <span className="px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold tabular-nums whitespace-nowrap">
                                                                {device.anomalyCount} {plural(device.anomalyCount, 'аномалия', 'аномалии', 'аномалий')}
                                                            </span>
                                                            <span className="px-1.5 py-0.5 rounded bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[10px] font-bold tabular-nums whitespace-nowrap">
                                                                макс. {device.maxChangePercent.toFixed(1).replace('.', ',')}%
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-slate-500 text-[11px] mt-1">
                                                            <MapPin className="w-3 h-3 flex-shrink-0" />
                                                            <span className="truncate">{formatAddress(device)}</span>
                                                            <span className="text-slate-600">•</span>
                                                            <span className="text-slate-500 whitespace-nowrap">{device.deviceType}</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {device.dates.slice(0, 5).map((date, di) => (
                                                                <span key={di} className="px-1.5 py-0.5 rounded bg-slate-700/60 border border-slate-600/50 text-slate-300 text-[10px] tabular-nums">{date}</span>
                                                            ))}
                                                            {device.dates.length > 5 && <span className="px-1.5 py-0.5 text-slate-500 text-[10px]">+{device.dates.length - 5}</span>}
                                                        </div>
                                                        {device.topParams?.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                                {device.topParams.map((p, pi) => (
                                                                    <span key={pi} className="px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/25 text-purple-300 text-[10px] font-mono">{p}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button onClick={() => goToAnalysis(device)}
                                                        className="flex-shrink-0 px-2.5 py-2 rounded-lg bg-blue-600/15 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 hover:text-blue-300 transition opacity-70 group-hover:opacity-100"
                                                        title="Открыть в «Сравнительный анализ»">
                                                        <ExternalLink className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default StatisticsPage;